import styled from "@emotion/styled";
import { type Placement } from "@popperjs/core";
import { type ReactElement, type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { ClickableDiv } from "./ClickableDiv";
import { PopoverContainer } from "./PopoverContainer";

interface PopoverProps {
  closeOnClick?: boolean;
  trigger?: ReactElement;
  triggerId?: string;
  children?: ReactNode;
  defaultIsOpen?: boolean;
  maxHeight?: string;
  placement?: Placement;
}

const Container = styled.div`
  position: relative;
`;

export const Popover = ({
  trigger,
  triggerId,
  children,
  closeOnClick,
  defaultIsOpen,
  maxHeight,
  placement,
}: PopoverProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen ?? false);
  const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(triggerRef, menuRef, {
    placement: placement ?? "auto",
    modifiers: [{ name: "offset", options: { offset: [10, 10] } }],
  });

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => {
    setIsOpen(false);
  });

  const triggerElement = triggerId ? document.getElementById(triggerId) : null;

  const height = `calc(100vh - ${
    triggerElement?.getBoundingClientRect().top
  }px - 70px)`;

  return (
    <Container>
      <ClickableDiv
        ref={setTriggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        {trigger}
      </ClickableDiv>
      {isOpen &&
        createPortal(
          <PopoverContainer
            ref={(el: HTMLDivElement) => {
              setMenuRef(el);
            }}
            style={styles.popper}
            {...attributes.popper}
            maxHeight={maxHeight}
            height={height}
          >
            <div
              ref={ref}
              onClick={(e) => {
                if (closeOnClick) {
                  setIsOpen(!isOpen);
                  e.stopPropagation();
                }
              }}
            >
              {children}
            </div>
          </PopoverContainer>,
          document.querySelector("#popper-portal")!
        )}
    </Container>
  );
};
